import { UseState } from '@unimpaired/utils'
import * as R from 'ramda'
import React, { isValidElement } from 'react'
import { twMerge } from 'tailwind-merge'
import { Modal, ModalType } from '../Modal'
import {
  DayScheduleProps,
  ScheduleEvent,
  ScheduleModalState,
} from './DaySchedule.types'
import { Agenda } from './Agenda'
import {
  isPlainModalContent,
  isScheduleEventList,
} from './schedule.modal.helpers'

export const DayScheduleEventDetailsModal = <T extends ScheduleEvent>(
  props: {
    eventDisplayedState: UseState<T | undefined>
    modalState: UseState<ScheduleModalState<T>>
  } & Pick<
    DayScheduleProps<T>,
    | 'modalType'
    | 'renderModalContent'
    | 'eventDescriptor'
    | 'onCreateNew'
    | 'timezone'
  >,
) => {
  const [eventDisplayed, setEventDisplayed] = props.eventDisplayedState
  const [modal, setModal] = props.modalState

  // a React element body pads itself
  const contentRendersItsOwnPadding =
    isPlainModalContent(modal.content) && isValidElement(modal.content.content)
  const padModalContent =
    !eventDisplayed && !!modal.content && !contentRendersItsOwnPadding

  // An overflow item opens like a tap on the schedule itself
  const openFromAgenda = props.renderModalContent
    ? (event: T) => {
        setModal((modal) => ({ ...modal, content: null }))
        setEventDisplayed(event)
      }
    : undefined

  const getModalTitle = () => {
    if (eventDisplayed) return eventDisplayed.name
    if (!modal.content) return undefined
    if (isPlainModalContent(modal.content)) return modal.content.title ?? undefined
    if (modal.content.length === 1) return modal.content[0].name
    return `${modal.content.length} Events`
  }

  const getModalType = (): ModalType => {
    if (!props.modalType) return 'modal'
    if (typeof props.modalType === 'string') return props.modalType
    if (!eventDisplayed) return 'modal'
    return props.modalType(eventDisplayed)
  }

  // prettier-ignore
  const omittedProps = [ 'condensed', 'modalContent', 'eventDescriptor', 'onClick', 'colStart', 'colWidth' ]

  return (
    <Modal
      type={getModalType()}
      className={twMerge(padModalContent && 'p-4')}
      title={getModalTitle()}
      isOpen={modal.open || !!eventDisplayed}
      onClose={() => {
        setEventDisplayed(undefined)
        // clear the list so it can't resurface next time
        setModal((modal) => ({ ...modal, open: false, content: null }))
      }}>
      {/*
        Three paths, in priority order:
        1. `renderModalContent` prop + a clicked event  -> render the custom body
        2. modal.content is PlainModalContent           -> render its `.content`
        3. modal.content is ScheduleEvent[]             -> render an Agenda of those events
        A clicked event always wins: a list or pane left in state from an
        earlier tap must not render under it.
      */}
      {eventDisplayed &&
        props.renderModalContent &&
        // @ts-expect-error DayScheduleEvent internal props are stripped by omit
        props.renderModalContent(R.omit(omittedProps, eventDisplayed), () =>
          setEventDisplayed(undefined),
        )}
      {!eventDisplayed && isPlainModalContent(modal.content) && modal.content.content}

      {!eventDisplayed && isScheduleEventList(modal.content) && (
        <Agenda
          events={modal.content}
          timezone={props.timezone}
          onAgendaItemClick={openFromAgenda}
        />
      )}
    </Modal>
  )
}
